import { useDeleteJobPart, useUpdateJobPart, type JobPart } from '@/hooks/useJobParts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useVisibility } from '@/hooks/useVisibility';

type PriceField = 'part_cost' | 'sell_price';

interface JobPartsTableProps {
  parts: JobPart[];
  jobId: string;
  editable?: boolean;
}

export function JobPartsTable({ parts, jobId, editable = false }: JobPartsTableProps) {
  const deletePart = useDeleteJobPart();
  const updatePart = useUpdateJobPart();
  const { formatMoney } = useCompanyLocale();
  const { canSeeProfit } = useVisibility();
  // In-progress edits keyed by part id, then by field; committed on blur.
  const [editing, setEditing] = useState<Record<string, Partial<Record<PriceField, string>>>>({});

  if (parts.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No parts added yet
      </div>
    );
  }

  const valueFor = (part: JobPart, field: PriceField) =>
    editing[part.id]?.[field] ?? part[field].toString();

  const setEdit = (partId: string, field: PriceField, value: string) =>
    setEditing(prev => ({ ...prev, [partId]: { ...prev[partId], [field]: value } }));

  const commit = (part: JobPart, field: PriceField) => {
    const next = parseFloat(valueFor(part, field));
    if (!isNaN(next) && next >= 0 && next !== part[field]) {
      updatePart.mutate({ partId: part.id, jobId, [field]: next });
    }
    setEditing(prev => {
      const row = { ...prev[part.id] };
      delete row[field];
      const all = { ...prev };
      if (Object.keys(row).length === 0) delete all[part.id]; else all[part.id] = row;
      return all;
    });
  };

  const priceCell = (part: JobPart, field: PriceField) =>
    editable ? (
      <Input
        type="number"
        step="0.01"
        min="0"
        value={valueFor(part, field)}
        onChange={(e) => setEdit(part.id, field, e.target.value)}
        onBlur={() => commit(part, field)}
        className="w-24"
      />
    ) : (
      formatMoney(part[field])
    );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Part Name</TableHead>
          <TableHead>Source</TableHead>
          <TableHead>Internal Cost</TableHead>
          <TableHead>Sell Price</TableHead>
          <TableHead>Qty</TableHead>
          <TableHead>Total</TableHead>
          {canSeeProfit && <TableHead>Margin</TableHead>}
          <TableHead>Notes</TableHead>
          <TableHead className="w-[80px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {parts.map((part) => {
          const margin = (part.sell_price - part.part_cost) * part.quantity;
          return (
            <TableRow key={part.id} className={part.part_source === 'tech_personal' ? 'bg-info/5' : ''}>
              <TableCell>{part.part_name}</TableCell>
              <TableCell>
                {part.part_source === 'tech_personal' ? (
                  <span className="text-xs bg-info/10 text-info px-2 py-1 rounded">
                    🔧 Tech
                  </span>
                ) : (
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">
                    📦 Company
                  </span>
                )}
              </TableCell>
              <TableCell>{priceCell(part, 'part_cost')}</TableCell>
              <TableCell>{priceCell(part, 'sell_price')}</TableCell>
              <TableCell>{part.quantity}</TableCell>
              <TableCell>{formatMoney(part.sell_price * part.quantity)}</TableCell>
              {canSeeProfit && (
                <TableCell className={margin < 0 ? 'text-destructive' : ''}>{formatMoney(margin)}</TableCell>
              )}
              <TableCell>{part.notes || '-'}</TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deletePart.mutate({ partId: part.id, jobId })}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
